import React, { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Platform, convertCssUnit } from '@staffino/primitive-controls';
import SettingsContext from '../settings-context';
import Slider, { LowerBoundLabel, UpperBoundLabel } from '../components/slider';

type Props = {
  name: string;
  value?: number;
  colorStyle?: string;
  lowerBoundLabel?: string;
  upperBoundLabel?: string;
  lowerBoundScore?: number;
  upperBoundScore?: number;
  highlighted?: boolean;
  onChange?: (score: number) => void;
} & React.ComponentProps<typeof Box>;

const SliderControl: React.ForwardRefExoticComponent<
  Props & React.RefAttributes<HTMLInputElement>
> = React.forwardRef(
  (
    {
      name,
      value,
      colorStyle,
      lowerBoundScore,
      upperBoundScore,
      lowerBoundLabel,
      upperBoundLabel,
      onChange,
      ...rest
    }: Props,
    ref: React.LegacyRef<HTMLInputElement>,
  ) => {
    const { t } = useTranslation();
    const { contentWidth } = useContext(SettingsContext);
    const step = 0.001;

    const boxWidth = Platform.select<number>({
      default: 1,
      native: contentWidth * 0.9,
    });

    const handleChange = useCallback(
      (score: number) => {
        // after releasing slider's thumb we want to jump it to the nearest possible score
        onChange?.(Math.round(score));
      },
      [onChange],
    );

    return (
      <Box {...rest}>
        <Slider
          ref={ref}
          name={name}
          value={value}
          step={step}
          lowerBoundScore={lowerBoundScore}
          upperBoundScore={upperBoundScore}
          colorStyle={colorStyle}
          width={boxWidth}
          onChange={handleChange}
        />
        <Box
          flexDirection={'row' as const}
          width={boxWidth}
          maxWidth={convertCssUnit('30rem')}
          my={0}
          mx="auto"
        >
          <LowerBoundLabel width="50%">
            {lowerBoundLabel || t('Poor')}
          </LowerBoundLabel>
          <UpperBoundLabel width="50%">
            {upperBoundLabel || t('Excellent')}
          </UpperBoundLabel>
        </Box>
      </Box>
    );
  },
);

export default SliderControl;
